import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';
import {
  isModuleEntitled,
  resolveModuleDisplayName,
  resolveModuleTerminology,
  type ModuleTermMap,
  type ModuleTerminology,
  type TerminologyModule,
} from '../utils/moduleLabels';

export type { TerminologyModule };

const FALLBACK_NAV_KEYS: Record<TerminologyModule, string> = {
  customer: 'nav.customers',
  vendor: 'nav.vendors',
  product: 'nav.products',
};

const FALLBACK_SINGULAR_KEYS: Record<TerminologyModule, string> = {
  customer: 'terminology.customer.singular',
  vendor: 'terminology.vendor.singular',
  product: 'terminology.product.singular',
};

const FALLBACK_PLURAL_KEYS: Record<TerminologyModule, string> = {
  customer: 'terminology.customer.plural',
  vendor: 'terminology.vendor.plural',
  product: 'terminology.product.plural',
};

/** Label for a terminology module: org displayName override, else i18n fallback. */
export function useModuleLabel(
  module: TerminologyModule,
  fallbackI18nKey?: string,
): string {
  const { t } = useTranslation();
  const orgFeatures = useSelector((state: RootState) => state.auth.orgFeatures);
  const key = fallbackI18nKey ?? FALLBACK_NAV_KEYS[module];
  return useMemo(() => {
    const override = resolveModuleDisplayName(orgFeatures, module);
    return override ?? t(key);
  }, [orgFeatures, module, key, t]);
}

/** Singular + plural terminology for a module. */
export function useModuleTerminology(module: TerminologyModule): ModuleTerminology {
  const { t } = useTranslation();
  const orgFeatures = useSelector((state: RootState) => state.auth.orgFeatures);
  return useMemo(
    () =>
      resolveModuleTerminology(orgFeatures, module, {
        singular: t(FALLBACK_SINGULAR_KEYS[module]),
        plural: t(FALLBACK_PLURAL_KEYS[module]),
      }),
    [orgFeatures, module, t],
  );
}

/** Map of all terminology modules — for template title substitution. */
export function useModuleTerminologyMap(): ModuleTermMap {
  const customer = useModuleTerminology('customer');
  const vendor = useModuleTerminology('vendor');
  const product = useModuleTerminology('product');
  return useMemo(
    () => ({ customer, vendor, product }),
    [customer, vendor, product],
  );
}

export function useModuleEntitled(module: TerminologyModule): boolean {
  const orgFeatures = useSelector((state: RootState) => state.auth.orgFeatures);
  const featureKeys = useSelector((state: RootState) => state.auth.featureKeys);
  return useMemo(
    () => isModuleEntitled(orgFeatures, featureKeys, module),
    [orgFeatures, featureKeys, module],
  );
}
